import store from 'State/store';
import { useZustandStore } from 'State/store';

const isNonEmptyObject = (value) => typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0;

const deepStateUpdate = (obj, keys, newState) => {
  if (keys.length === 0) {
    if (isNonEmptyObject(newState) && isNonEmptyObject(obj)) {
      return { ...obj, ...newState };
    }
    return newState;
  }

  return {
    ...obj,
    [keys[0]]: deepStateUpdate(obj[keys[0]], keys.slice(1), newState),
  };
};
/* 
https://github.com/pmndrs/zustand/blob/517524d2b05843c6a26d2713af08cc0ca1f62f3c/docs/guides/immutable-state-and-merging.md
Zustand automatically merges state at top level only in its setState function.
This helper function handles merging nested state.

E.g. setState('integrations.sheerId', { verificationId: '123456' })
*/
export const setState = (keyString = '', newState = {}) => {
  useZustandStore.setState((state) => {
    const keys = keyString.split('.');

    return deepStateUpdate(state, keys, newState);
  });
};

const deepStateReplace = (obj, keys, updateFunc) => {
  if (keys.length === 0) {
    return updateFunc(obj);
  }

  return {
    ...obj,
    [keys[0]]: deepStateReplace(obj[keys[0]], keys.slice(1), updateFunc),
  };
};
/**
 * This helper function is similar to `setState`, but allows you greater control in choosing
 * whether to merge or replace existing state.
 *
 * @callback UpdateFunc
 * @param {Object} prevState - Previous state object in the `keyString` path
 * @returns {Object} - New state to replace everything inside the `keyString` path
 *
 * @param {string} keyString - The state path to update. Can be as deeply nested as necessary.
 * Should be separated by dots (e.g. "payment.methods.cards")
 * @param {UpdateFunc} updateFunc - Function specifying how the new state should be derived from the previous state
 *
 * @example
 * // Consider the current state is `{ payments: { methods: { cash: true, cards: ['mastercard'] } }, user: {...} }`
 * replaceState('payments.methods', prevState => ({ cards: [...prevState.cards, 'visa'] }));
 * // State gets updated to `{ payments: { methods: { cards: ['mastercard', 'visa']} }, user: {...}}
 * // User doesn't get changed, since everything above the `keyString` path gets merged like the `setState`
 */
export const replaceState = (keyString = '', updateFunc) => {
  useZustandStore.setState((state) => {
    const keys = keyString.split('.');

    return deepStateReplace(state, keys, updateFunc);
  });
};

export const getStoreState = (key) => {
  const state = { ...store?.getState(), ...useZustandStore?.getState() };
  return key ? state?.[key] : state;
};

export const getStoreDispatch = () => {
  return store?.dispatch;
};

export const getDefaultConfig = (key) => {
  const defaultConfig = getStoreState('appConfig')?.defaultConfig;
  return key ? defaultConfig?.[key] : defaultConfig;
};
